import { Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, ScrollView, View } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useToastCreator } from '../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { useProductManagementActions } from '../../store/ProducManagement/actions';
import {
  batchActionSelectedProductIds,
  batchActionState,
  modalSelectedItemsState,
} from '../../store/ProducManagement/atom';
import { ProductsManagementAvaliableToControlQuery } from '../../store/ProducManagement/selector';
import {
  DeleteBatchControlProductPayload,
  InsertControlProductPayload,
} from '../../store/ProducManagement/types';

import { DynamicProductsTable } from './components/DynamicProductsTable';
import { Filters } from './components/Filters';
import { Header } from './components/Header';
import { ModalIncludeExcludeBlock } from './components/ModalIncludeExcludeBlock';
import { Tabs } from './components/Tabs';
import {
  CreateBatchBlockModelContent,
  DeleteBatchBlockModelContent,
  ProductsTabs,
  ProductType,
} from './constants';
import { useFilterProductsManagement } from './hooks/useFilterProductsManagement';

export const ProductsManagement = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [batchAction, setBatchAction] = useRecoilState(batchActionState);
  const [modalSelectedItems, setModalSelectedItems] = useRecoilState(modalSelectedItemsState);
  const [batchActionSelectedIds, setBatchActionSelectedIds] = useRecoilState(
    batchActionSelectedProductIds
  );

  const { insertBatchControlProduct, deleteBatchControlProduct } = useProductManagementActions();
  const { searchTerm, productBlockFilter, handleClearFilter } = useFilterProductsManagement();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast } = useToastCreator('toast-products-management-list');

  const queryParams = useMemo(() => {
    return {
      productType: ProductsTabs[selectedTabIndex].productType,
      filter: searchTerm,
      hasFavorites: false,
      blocked: productBlockFilter === 'blocked',
      unblocked: productBlockFilter === 'unblocked',
    };
  }, [selectedTabIndex, searchTerm, productBlockFilter]);

  const { data, error, loading, refresher } = useRecoilQuery(
    ProductsManagementAvaliableToControlQuery(queryParams),
    {
      cachePolicy: 'no-cache',
    }
  );

  useEffect(() => {
    if (batchAction === 'block') {
      navigate('ProductsManagementBlockModal', {
        productTypeId: ProductsTabs[selectedTabIndex].productType,
      });
    }

    if (batchAction === 'unblock') {
      setIsModalOpen(true);
    }
  }, [batchAction]);

  useEffect(() => {
    if (modalSelectedItems?.destinationId) {
      setIsModalOpen(true);
    }
  }, [modalSelectedItems]);

  useEffect(() => {
    setIsModalOpen(false);
    setModalSelectedItems(null);
    setBatchAction(null);
    setBatchActionSelectedIds([]);
    handleClearFilter();
  }, [selectedTabIndex]);

  useEffect(() => {
    setBatchAction(null);
    setBatchActionSelectedIds([]);
    refresher();
  }, [productBlockFilter, refresher]);

  useEffect(() => {
    if (!isModalOpen) {
      setModalSelectedItems(null);
      setBatchAction(null);
    }
  }, [isModalOpen]);

  const handleDetails = (productId: number, managerImage: string, productName: string) => {
    navigate('ProductsManagementDetails', {
      managerImage,
      productName,
      productId,
      productFilterIdAction: 2,
      productTypeId: ProductsTabs[selectedTabIndex].productType,
      productTypeName: ProductsTabs[selectedTabIndex].title,
    });
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setIsModalOpen(false);
    }
  };

  const handleConfirmIncludeExclude = async () => {
    const productTypeId = ProductsTabs[selectedTabIndex].productType;

    if (batchAction === 'block' && modalSelectedItems) {
      setIsLoading(true);

      const payload: InsertControlProductPayload = {
        productTypeId,
        productFilterActionId: 2,
        productFilterPlatformId: modalSelectedItems.platformId,
        productFilterDestinationId: modalSelectedItems.destinationId,
        batchItemsSelected: batchActionSelectedIds,
        productsList: modalSelectedItems.productList?.length
          ? modalSelectedItems.productList
          : undefined,
        ...(modalSelectedItems.isClientOrAdvisor === 'advisor' && {
          advisorId: modalSelectedItems.clientOrAdvisorNameId?.id,
        }),
        ...(modalSelectedItems.isClientOrAdvisor === 'client' && {
          accountId: modalSelectedItems.clientOrAdvisorNameId?.id,
        }),
      };

      await insertBatchControlProduct(payload)
        .then(() => {
          const message =
            modalSelectedItems.isClientOrAdvisor === 'client'
              ? `Cliente(a) ${modalSelectedItems.clientOrAdvisorNameId?.name} foi bloqueado com sucesso!`
              : modalSelectedItems.isClientOrAdvisor === 'advisor'
              ? `Assessor(a) ${modalSelectedItems.clientOrAdvisorNameId?.name} foi bloqueado com sucesso!`
              : 'O parceiro foi bloqueado com sucesso!';

          showToast('success', 'Tudo certo!', message);
          setBatchActionSelectedIds([]);
          refresher();
        })
        .finally(() => {
          setIsModalOpen(false);
          setIsLoading(false);
        });
    }

    if (batchAction === 'unblock') {
      setIsLoading(true);

      const payload: DeleteBatchControlProductPayload = {
        actionProductFilterId: 2,
        productTypeId,
        ...(productTypeId === ProductType.FUNDS && {
          productListId: batchActionSelectedIds.map((id) => Number(id)),
        }),
        ...(productTypeId === ProductType.FIXED_INCOME && {
          fixedIncomeIssuerListId: batchActionSelectedIds.map((id) => Number(id)),
        }),
      };

      await deleteBatchControlProduct(payload)
        .then(() => {
          showToast('success', 'Tudo certo!', 'Items removidos com sucesso!');
          setBatchActionSelectedIds([]);
          refresher();
        })
        .finally(() => {
          setIsModalOpen(false);
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <ScrollView>
        <View
          h="full"
          width="100%"
          minW="container"
          flex={1}
          px={{ base: 0, sm: Spacing['inline.sm'], lg: Spacing['inline.sm'] }}>
          <Header isMobile={isMobile} />
          <Box
            flex={1}
            background={{ base: 'transparent', sm: Color['neutral.white'] }}
            borderRadius={BoxRadius.sm}>
            {!isMobile && (
              <VStack
                py={Spacing['stack.xxs']}
                borderBottomWidth={'1px'}
                borderBottomColor="neutral.light"
                px={Spacing['inline.xxs']}>
                <HStack alignItems="center" justifyContent="space-between">
                  <Text
                    fontSize={Typography['fontsize.lg']}
                    variant="semibold"
                    color="brand.primary.principal">
                    Selecione o tipo de produto
                  </Text>
                </HStack>
              </VStack>
            )}
            <VStack
              flex={1}
              px={Spacing['inline.xxs']}
              py={{ base: Spacing['stack.nano'], sm: Spacing['stack.xxs'] }}
              space={Spacing['stack.xxs']}>
              <Tabs
                selectedTabIndex={selectedTabIndex}
                productsTabs={ProductsTabs}
                handleChangeTab={(index) => setSelectedTabIndex(index)}
                loading={loading}
                isMobile={isMobile}
              />
              <Filters isLoading={loading} handleChangeTab={setSelectedTabIndex} />
              <DynamicProductsTable
                selectedTabIndex={selectedTabIndex}
                loading={loading}
                data={data}
                error={error}
                onDetails={handleDetails}
                refresher={refresher}
              />
            </VStack>
          </Box>
        </View>
      </ScrollView>

      <ModalIncludeExcludeBlock
        isModalOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmIncludeExclude}
        isLoading={isLoading}>
        {batchAction === 'unblock' && (
          <DeleteBatchBlockModelContent itemsAmount={batchActionSelectedIds.length} />
        )}
        {batchAction === 'block' && modalSelectedItems && (
          <CreateBatchBlockModelContent {...modalSelectedItems} />
        )}
      </ModalIncludeExcludeBlock>
    </>
  );
};
